import { createClient } from '@supabase/supabase-js';
import { errorMonitoring } from '../services/errorMonitoring';
import { createRetryableClient } from './retryableSupabaseClient';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

const supabaseClient = createClient(supabaseUrl, supabaseAnonKey)

// Enhanced Supabase client with error interception
const createEnhancedClient = (client) => {
  return new Proxy(client, {
    get(target, prop) {
      const original = target[prop];
      
      // Intercept 'from' method to wrap queries
      if (prop === 'from') {
        return (table) => {
          const queryBuilder = original.call(target, table);
          return wrapQueryBuilder(queryBuilder, table);
        };
      }
      
      return original;
    },
  });
};

// Wrap query builder methods with error logging
const wrapQueryBuilder = (queryBuilder, table) => {
  const methodsToWrap = ['select', 'insert', 'update', 'delete', 'upsert'];
  
  methodsToWrap?.forEach(method => {
    const original = queryBuilder?.[method];
    if (original) {
      queryBuilder[method] = function(...args) {
        const result = original?.apply(this, args);
        
        // Store operation type for error logging
        if (!result?._errorContext) {
          result._errorContext = {};
        }
        result._errorContext.table = table;
        result._errorContext.operation = method?.toUpperCase();
        
        return wrapQueryBuilder(result, table);
      };
    }
  });
  
  // Wrap terminal methods (single, maybeSingle, etc.)
  const terminalMethods = ['single', 'maybeSingle'];
  terminalMethods?.forEach(method => {
    const original = queryBuilder?.[method];
    if (original) {
      queryBuilder[method] = async function(...args) {
        const result = await original?.apply(this, args);
        
        // Log error if present
        if (result?.error && result?.error?.code !== 'PGRST116') {
          const context = this._errorContext || {};
          errorMonitoring?.handleQueryError(
            result?.error,
            {
              table: context?.table,
              operation: context?.operation,
              query: 'Supabase query',
            },
            {},
            false // Don't show toast here, let calling code handle it
          );
        }
        
        return result;
      };
    }
  });
  
  return queryBuilder;
};

export const supabase = createEnhancedClient(supabaseClient);

// Create retryable Supabase client with default configuration
export const retryableSupabase = createRetryableClient(supabaseClient, {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
});

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase?.auth?.getUser()
  if (error) throw error
  return user
}

// Helper function to check if user has active subscription
export const hasActiveSubscription = async (userId) => {
  const { data, error } = await supabase?.from('subscriptions')?.select('status')?.eq('user_id', userId)?.eq('status', 'active')?.single()

  if (error && error?.code !== 'PGRST116') throw error
  return !!data
}
